"use client";

// A two-question prompt shown once, in front of a retailer link: where the
// visitor found us, and how easy the site was to use. Attribution is the thing
// we otherwise have no way to measure — the click leaves for Amazon and the
// referrer is long gone by the time anything converts.
//
// The rules that keep it from being a tax on buying:
//   - asked once per person (see the storage keys below), never again after an answer;
//   - skipping is always one click away and goes straight through;
//   - the retailer link opens on the same user gesture that submits, so the
//     answer never sits between the visitor and the thing they clicked. The POST
//     is fire-and-forget: a failed write loses a data point, not a sale.

import { useEffect, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { sendGAEvent } from "@next/third-parties/google";
import { AffiliateDisclosure } from "@/components/affiliate-disclosure";

const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://laptop-search-tool-dev.onrender.com";

// Values must match ALLOWED_SOURCES in api/feedback/model.py — the API rejects
// anything else, which is what keeps the dashboard breakdown clean.
export const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: "reddit", label: "Reddit" },
  { value: "google", label: "Google search" },
  { value: "youtube", label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "x", label: "X / Twitter" },
  { value: "facebook", label: "Facebook" },
  { value: "referral", label: "A friend told me" },
  { value: "other", label: "Somewhere else" },
];

const RATINGS = [1, 2, 3, 4, 5];

// Answering is permanent; skipping only lasts the session, so someone who
// brushed it off once isn't written off forever — but also isn't asked twice
// in the same sitting.
const ANSWERED_KEY = "plample:buyer-survey:answered";
const SKIPPED_KEY = "plample:buyer-survey:skipped";

export type BuyTarget = {
  url: string;
  /** Shown on the continue button, e.g. "Amazon" or the seller's name. */
  retailer?: string | null;
  laptopName?: string | null;
  laptopSlug?: string | null;
  country?: string | null;
};

/** Put this on a retailer anchor's onClick; it decides whether to intercept. */
export type BuyClickHandler = (e: MouseEvent<HTMLAnchorElement>, target: BuyTarget) => void;

/** Storage access throws in some privacy modes; a blocked read just means we ask. */
function readFlag(store: "local" | "session", key: string): boolean {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    return s.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(store: "local" | "session", key: string) {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    s.setItem(key, "1");
  } catch {
    /* private mode — the visitor may be asked again, which is the harmless failure */
  }
}

/** Open the retailer in a new tab without handing it a reference back to us.
 *  Called synchronously from the click that triggered it, so it isn't treated
 *  as an unsolicited popup. */
function openTarget(url: string) {
  const w = window.open(url, "_blank");
  if (w) {
    w.opener = null;
  } else {
    // Popup blocked despite the gesture — same-tab is better than a dead click.
    window.location.href = url;
  }
}

/**
 * Wraps a set of buy links on one page. Returns a click handler to put on each
 * retailer anchor and the modal element to render alongside them.
 *
 * The anchors keep their real `href`, so middle-click, "open in new tab" and
 * crawlers behave exactly as before — the survey only intercepts a plain
 * left-click, and only when we haven't asked yet.
 */
export function useBuyerSurveyGate(context?: {
  laptopName?: string | null;
  laptopSlug?: string | null;
  country?: string | null;
}) {
  const [pending, setPending] = useState<BuyTarget | null>(null);

  const handleBuyClick: BuyClickHandler = (e, target) => {
    // Every buy click is counted, before any of the early returns below. A no-op
    // until GA has loaded (sendGAEvent only pushes onto window.dataLayer).
    if (process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
      sendGAEvent("event", "buy_click", {
        retailer: target.retailer ?? undefined,
        laptop_slug: target.laptopSlug ?? context?.laptopSlug ?? undefined,
        country: target.country ?? context?.country ?? undefined,
        link_url: target.url,
      });
    }

    // Leave modified clicks (new tab, new window, download) to the browser.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    if (readFlag("local", ANSWERED_KEY) || readFlag("session", SKIPPED_KEY)) return;

    e.preventDefault();
    setPending({
      ...target,
      laptopName: target.laptopName ?? context?.laptopName ?? null,
      laptopSlug: target.laptopSlug ?? context?.laptopSlug ?? null,
      country: target.country ?? context?.country ?? null,
    });
  };

  const gate = pending ? (
    <BuyerSurveyModal target={pending} onClose={() => setPending(null)} />
  ) : null;

  return { handleBuyClick, gate };
}

function BuyerSurveyModal({ target, onClose }: { target: BuyTarget; onClose: () => void }) {
  const [source, setSource] = useState("");
  const [rating, setRating] = useState<number | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const retailer = target.retailer || "the retailer";

  const submit = () => {
    if (!source || rating == null) return;
    // Fired without awaiting, and with keepalive so it survives the tab losing
    // focus to the retailer a moment later.
    try {
      void fetch(`${API_BASE}/feedback/buyer-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          source,
          ease_rating: rating,
          laptop_name: target.laptopName ?? null,
          laptop_slug: target.laptopSlug ?? null,
          retailer: target.retailer ?? null,
          destination_url: target.url,
          page_path: window.location.pathname,
          country: target.country ?? null,
        }),
      }).catch(() => {});
    } catch {
      /* never block the click on our own telemetry */
    }
    writeFlag("local", ANSWERED_KEY);
    openTarget(target.url);
    onClose();
  };

  const skip = () => {
    writeFlag("session", SKIPPED_KEY);
    openTarget(target.url);
    onClose();
  };

  const ready = Boolean(source) && rating != null;

  // z-[60] puts this above the sellers drawer (z-50), where half these clicks start.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Quick question before you go"
    >
      <div
        className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
        onClick={skip}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-3xl bg-card ring-1 ring-border/60 shadow-2xl">
        <div className="flex items-start gap-3 border-b border-border/60 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold leading-snug tracking-tight">
              Two quick questions
            </h3>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Takes five seconds, then we&apos;ll send you to {retailer}.
            </p>
          </div>
          <button
            onClick={skip}
            aria-label="Close"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="size-4">
              <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div>
            <label
              htmlFor="buyer-survey-source"
              className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
            >
              How did you find Plample?
            </label>
            <select
              id="buyer-survey-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="mt-2 h-11 w-full rounded-xl border border-border bg-surface px-3 text-sm text-foreground outline-none transition-colors focus:border-foreground/40"
            >
              <option value="" disabled>
                Choose one…
              </option>
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              How easy was Plample to use?
            </p>
            <div className="mt-2 flex gap-1.5" role="group" aria-label="Ease of use rating">
              {RATINGS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  aria-pressed={rating === n}
                  className={`h-11 flex-1 rounded-xl text-sm font-semibold tabular-nums transition-colors ${
                    rating === n
                      ? "bg-brand text-brand-foreground"
                      : "border border-border text-foreground hover:border-foreground/40 hover:bg-surface"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground">
              <span>Hard</span>
              <span>Very easy</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 border-t border-border/60 px-5 py-4">
          {/* This is the last thing on screen before the visitor leaves for the
              shop, and we know exactly which shop that is. Worth saying again
              here rather than trusting that they read it on the page behind. */}
          <AffiliateDisclosure retailer={target.retailer || null} className="pb-1" />
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-brand text-sm font-semibold text-brand-foreground shadow-sm transition-all hover:opacity-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Submit and continue to {retailer}
            <svg viewBox="0 0 16 16" fill="currentColor" className="size-4">
              <path
                fillRule="evenodd"
                d="M4.22 11.78a.75.75 0 0 1 0-1.06L8.94 6H5.75a.75.75 0 0 1 0-1.5h5.5a.75.75 0 0 1 .75.75v5.5a.75.75 0 0 1-1.5 0V7.56l-4.72 4.72a.75.75 0 0 1-1.06 0Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={skip}
            className="h-9 w-full text-center text-[11px] font-medium text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
          >
            No thanks, take me to {retailer}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
